/**
 * Programs and activities — server state.
 *
 * One hooks module for both, mirroring the single backend service: creating an
 * activity changes what its program's detail screen reports (the activity
 * count), so the two caches have to be invalidated together and doing that from
 * two files means one of them eventually forgets.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  createActivity,
  createProgram,
  deleteActivity,
  deleteProgram,
  fetchActivities,
  fetchActivity,
  fetchProgram,
  fetchPrograms,
  updateActivity,
  updateProgram,
} from '../services/api/programs';
import type {
  ActivityBody,
  ActivityListQuery,
  NewActivityBody,
  ProgramBody,
  ProgramListQuery,
} from '../services/api/programs';

import { activityKeys, metricsKeys, programKeys } from './queryKeys';

export { activityKeys, programKeys };

// ---------------------------------------------------------------------------
// Programs
// ---------------------------------------------------------------------------

export function usePrograms(params: ProgramListQuery) {
  return useQuery({
    queryKey: programKeys.list(params),
    queryFn: () => fetchPrograms(params),
  });
}

export function useProgram(id: string | undefined) {
  return useQuery({
    queryKey: programKeys.detail(id ?? ''),
    queryFn: () => fetchProgram(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateProgram() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ProgramBody) => createProgram(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      toast.success('Program created.');
    },
    onError: (error) => toast.error(error, 'Could not create the program.'),
  });
}

export function useUpdateProgram(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ProgramBody) => updateProgram(id, body),
    onSuccess: () => {
      // Invalidated rather than written into the cache: the detail carries
      // `activity_count`, which a PATCH response does not include, so setting it
      // directly would drop the count off the screen.
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
      toast.success('Program saved.');
    },
    onError: (error) => toast.error(error, 'Could not save the program.'),
  });
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export function useActivities(params: ActivityListQuery) {
  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () => fetchActivities(params),
  });
}

export function useActivity(id: string | undefined) {
  return useQuery({
    queryKey: activityKeys.detail(id ?? ''),
    queryFn: () => fetchActivity(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateActivity() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: NewActivityBody) => createActivity(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
      // The program's activity count just changed.
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      toast.success('Activity created.');
    },
    onError: (error) => toast.error(error, 'Could not create the activity.'),
  });
}

export function useUpdateActivity(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ActivityBody) => updateActivity(id, body),
    onSuccess: (activity) => {
      queryClient.setQueryData(activityKeys.detail(id), activity);
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
      toast.success('Activity saved.');
    },
    onError: (error) => toast.error(error, 'Could not save the activity.'),
  });
}

// ---------------------------------------------------------------------------
// Deletion (M4)
// ---------------------------------------------------------------------------

/**
 * Delete a program and everything under it.
 *
 * Invalidates far more than the program: the cascade marks its activities
 * deleted, which changes the activity list, and their attendance stops counting,
 * which changes every figure on the dashboard. Leaving any of those showing the
 * old numbers is how an admin concludes the delete did not work and does it
 * again.
 */
export function useDeleteProgram(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => deleteProgram(id, reason),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
      void queryClient.invalidateQueries({ queryKey: metricsKeys.all });
      // The programme is deleted; what is under it is cancelled. Said as two
      // verbs rather than one, because they are two different outcomes and a
      // staff member reading 'deleted' about an activity would look for it in
      // the wrong place.
      toast.success(
        result.activities_cancelled === 0
          ? `${result.name} has been deleted.`
          : `${result.name} has been deleted, and its ${result.activities_cancelled} ${
              result.activities_cancelled === 1 ? 'activity' : 'activities'
            } cancelled.`
      );
    },
    onError: (error) => toast.error(error, 'Could not delete the program.'),
  });
}

export function useDeleteActivity(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => deleteActivity(id, reason),
    onSuccess: (activity) => {
      queryClient.setQueryData(activityKeys.detail(id), activity);
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
      // The program counts its activities, and the dashboard counted this
      // one's hours until a moment ago.
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      void queryClient.invalidateQueries({ queryKey: metricsKeys.all });
      toast.success(`${activity.name} has been deleted.`);
    },
    onError: (error) => toast.error(error, 'Could not delete the activity.'),
  });
}
